import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useState } from "react";

import { conversationQueryKey } from "../../../agent";
import { queryKey } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { OnboardingModule, PersonMemory } from "../../../api/types";
import { setModuleStatus } from "../profile/memoryEdits";

export interface ModuleActions {
  /** The module an action is running for. */
  busyModule: string | null;
  error: string | null;
  /** Start it: marks it started and asks the tutor to begin. */
  take: (module: OnboardingModule) => Promise<void>;
  /** Carry on where the person stopped. */
  resume: (module: OnboardingModule) => Promise<void>;
  /** Go through a finished or skipped module again. */
  again: (module: OnboardingModule) => Promise<void>;
  /** Answer the module's check first; if it is right the module counts as known. */
  skipByShowing: (module: OnboardingModule) => Promise<void>;
  skip: (module: OnboardingModule) => Promise<void>;
}

/** The sentence that goes to the tutor for each step. The tutor teaches in the conversation; the screen only asks. */
export const MODULE_MESSAGES = {
  take: (m: OnboardingModule) => `I would like to take module ${m.module}: ${m.title}.`,
  resume: (m: OnboardingModule) => `Please carry on with module ${m.module}: ${m.title}, where I stopped.`,
  again: (m: OnboardingModule) => `I would like to go through module ${m.module}: ${m.title} again.`,
  skipByShowing: (m: OnboardingModule) => `I would like to skip module ${m.module}: ${m.title} by showing that I know it. Please ask me the check first.`,
} as const;

/** The admin's only module. The server gives members their list; the admin has no discipline to ask. */
export function adminModules(memory: PersonMemory | undefined): OnboardingModule[] {
  return [
    {
      module: "A1",
      title: "The admin's job: accounts, the model connection and the disciplines",
      status: memory?.modules.A1 ?? "not_started",
      can_skip_by_showing: true,
    },
  ];
}

interface Options {
  /** A member of a discipline, or the admin (who has no discipline and a memory of their own). */
  owner: "member" | "admin";
  conversationKey: string;
}

/**
 * What the module buttons do. A member's module status is set with `set_module_status`; the admin's is part of the
 * admin's own memory. Teaching itself happens in the conversation, so most buttons also send the tutor a sentence.
 */
export function useModuleActions({ owner, conversationKey }: Options): ModuleActions {
  const queryClient = useQueryClient();
  const setStatus = useApiCommand("set_module_status");
  const editAdmin = useApiCommand("edit_admin_memory");
  const postMember = useApiCommand("post_message");
  const postAdmin = useApiCommand("post_admin_message");
  const [busyModule, setBusyModule] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = async (module: string, value: string) => {
    if (owner === "admin") {
      const reply = await editAdmin.mutateAsync(setModuleStatus(module, value as PersonMemory["modules"][string]));
      queryClient.setQueryData(queryKey("admin_memory", {}) as unknown as QueryKey, reply.data);
    } else {
      const reply = await setStatus.mutateAsync({ module, status: value });
      queryClient.setQueryData(queryKey("onboarding", {}) as unknown as QueryKey, reply.data);
    }
  };

  const say = async (text: string) => {
    if (owner === "admin") await postAdmin.mutateAsync({ key: conversationKey, text });
    else await postMember.mutateAsync({ key: conversationKey, text });
    void queryClient.invalidateQueries({ queryKey: conversationQueryKey(conversationKey, owner === "admin") });
  };

  const run = async (module: OnboardingModule, steps: () => Promise<void>) => {
    setBusyModule(module.module);
    setError(null);
    try {
      await steps();
    } catch (failure) {
      setError(refusalText(failure));
    } finally {
      setBusyModule(null);
    }
  };

  return {
    busyModule,
    error,
    take: (m) =>
      run(m, async () => {
        await status(m.module, "started");
        await say(MODULE_MESSAGES.take(m));
      }),
    resume: (m) => run(m, () => say(MODULE_MESSAGES.resume(m))),
    again: (m) =>
      run(m, async () => {
        await status(m.module, "started");
        await say(MODULE_MESSAGES.again(m));
      }),
    skipByShowing: (m) => run(m, () => say(MODULE_MESSAGES.skipByShowing(m))),
    skip: (m) => run(m, () => status(m.module, "skipped")),
  };
}
